// 2026-09-24 - FormuLearning - 격자 계산 제외·라운드 격자 snapshot 헬퍼 (이슈 #76 G5)
// 파일 위치: frontend/src/features/step4/gridExclusion.ts
// 목적: 라운드가 남긴 격자 snapshot(`round.meta.grid`, M901-30)을 읽고, 계산 제외(M901-29)를
//       결과 입력 격자·Next Optimization 기본값에 적용한다. 서버 `lot/round_service.py` 의
//       `round_exclusion`·`input_grid`·`effective_exclusion` 과 같은 규칙이다.
//
// 제외는 **이후 라운드의 기본값**으로 이어진다(Q-260902-07 B, 사용자 260924) — 뺀 이유가 «용출이
// 충분해서» 이므로 최적화가 진행돼도 유지된다고 본다. 새 run 에서 추가·수정·해제할 수 있다.

import type { PhProfile } from '../../api/step3'
import type { RoundInfo } from '../../api/round'

export interface GridExclusion {
  excluded_time_points: number[]
  excluded_solutions: string[]
}

/** `round.meta.grid` — run 시점 기준 Lot 격자(제외 전 원본) + 계산 제외. */
export interface RoundGrid extends GridExclusion {
  lot_id: string | null
  time_points: number[]
  solutions: string[]
}

export const NO_EXCLUSION: GridExclusion = { excluded_time_points: [], excluded_solutions: [] }

/** 용출액 식별자 정규화 — 서버 `f2.solution_key` 와 같다('pH 6.8'·'6.80'·6.8 → '6.8'). */
export function solutionKey(raw: string | number | null | undefined): string {
  if (raw == null) return ''
  const text = String(raw).trim()
  const n = Number(text.toLowerCase().replace('ph', '').trim())
  return text !== '' && Number.isFinite(n) ? String(n) : text
}

/** 그 라운드의 격자 snapshot. 기록 전 라운드(#76 이전)는 null. */
export function roundGrid(r: Pick<RoundInfo, 'meta'> | null | undefined): RoundGrid | null {
  const g = (r?.meta as { grid?: Partial<RoundGrid> } | undefined)?.grid
  if (!g || (!g.time_points?.length && !g.solutions?.length)) return null
  return {
    lot_id: g.lot_id ?? null,
    time_points: g.time_points ?? [],
    solutions: g.solutions ?? [],
    excluded_time_points: g.excluded_time_points ?? [],
    excluded_solutions: g.excluded_solutions ?? [],
  }
}

/** 그 라운드의 계산 제외. snapshot 이 없으면 제외 없음. */
export function roundExclusion(r: Pick<RoundInfo, 'meta'> | null | undefined): GridExclusion {
  const g = roundGrid(r)
  return g ? { excluded_time_points: g.excluded_time_points, excluded_solutions: g.excluded_solutions } : NO_EXCLUSION
}

/** **유효 제외** = 최신 optimization round 의 제외 — 새 run 의 기본값이자 저장 f2 의 기준(G1). */
export function carriedExclusion(rounds: Pick<RoundInfo, 'meta' | 'round_no' | 'round_type'>[]): GridExclusion {
  const opt = rounds.filter((r) => r.round_type === 'optimization')
  if (opt.length === 0) return NO_EXCLUSION
  return roundExclusion(opt.reduce((a, b) => (b.round_no > a.round_no ? b : a)))
}

export function hasExclusion(ex: GridExclusion): boolean {
  return ex.excluded_time_points.length > 0 || ex.excluded_solutions.length > 0
}

/**
 * 대조약 프로파일에서 제외 시점·용출액을 뺀다 — 결과 **입력** 격자의 뼈대(M901-29 «입력칸에서도 뺀다»).
 * 저장된 배치 프로파일은 건드리지 않는다(삭제 아님).
 */
export function applyExclusion(profiles: PhProfile[], ex: GridExclusion): PhProfile[] {
  if (!hasExclusion(ex)) return profiles
  const sols = new Set(ex.excluded_solutions.map(solutionKey))
  const times = new Set(ex.excluded_time_points.map(Number))
  return profiles
    .filter((p) => !sols.has(solutionKey(p.ph)))
    .map((p) => ({ ...p, time_points: (p.time_points ?? []).filter((tp) => !times.has(Number(tp.time))) }))
}

const MIN_POINTS = 3   // 서버 `f2.MIN_POINTS` — f2 성립 최소 시점 수

/** 화면 검증 — 서버 `normalize_exclusion` 과 같은 기준(남는 용출액 ≥ 1 · 남는 시점 ≥ 3). 통과하면 null. */
export function exclusionProblem(
  grid: { time_points: number[]; solutions: string[] } | null, ex: GridExclusion,
): string | null {
  if (!grid || !hasExclusion(ex)) return null
  const sols = new Set(ex.excluded_solutions.map(solutionKey))
  const times = new Set(ex.excluded_time_points.map(Number))
  if (grid.solutions.every((s) => sols.has(solutionKey(s)))) return '용출액을 모두 제외할 수 없습니다 — 하나 이상 남기십시오.'
  const left = grid.time_points.filter((t) => Number(t) > 0 && !times.has(Number(t))).length
  if (left < MIN_POINTS) return `f2 계산에는 시점이 ${MIN_POINTS}개 이상 필요합니다 — 제외 뒤 ${left}개가 남습니다.`
  return null
}

/** 제외 요약 — «30분 · pH 1.2 제외» 형태. 없으면 빈 문자열. */
export function describeExclusion(ex: GridExclusion): string {
  const parts = [
    ...ex.excluded_time_points.map((t) => `${t}분`),
    ...ex.excluded_solutions.map((s) => `pH ${solutionKey(s)}`),
  ]
  return parts.join(' · ')
}
